import { InfiniteData } from '@tanstack/react-query';

import { productCategoryFilter } from '~/shared/constants/productGrid';

import {
  ProductsV2Aggregation,
  ProductsV2AggregationItem,
  UnifiedFacet,
} from '../types/filters';
import { UnifiedProduct } from '../types/product';
import { buildImageServiceUrlFromSku } from '../utils/images';

const categoryIdToLabel: Record<string, string> = {};
productCategoryFilter.items.forEach(
  (item: { value: number; label: string }) => {
    categoryIdToLabel[String(item.value)] = item.label;
  }
);

export interface NormalizedProductsV2InfiniteData<T> extends InfiniteData<T> {
  products?: UnifiedProduct[];
  facets?: UnifiedFacet[];
}

/**
 * @param response - Infinite query response from useProductsV2()
 * @returns {Object} - Transformed response with flattened products array and facets
 */
export const normalizeProductsV2Response = (
  response: InfiniteData<any>
): NormalizedProductsV2InfiniteData<any> => {
  const productsV2Aggregations =
    response?.pages?.[0]?.initialProducts?.aggregations;

  return {
    ...response,
    facets:
      productsV2Aggregations
        ?.map((facet: ProductsV2Aggregation) => ({
          display_name: facet.label,
          hidden: false,
          name: facet.field,
          options: facet?.items
            ?.filter(
              (item: ProductsV2AggregationItem) =>
                facet.field !== 'default_product_category_id' ||
                Boolean(categoryIdToLabel[item.value])
            )
            ?.map((item: ProductsV2AggregationItem) => ({
              count: item.filterCount,
              display_name:
                facet.field === 'default_product_category_id'
                  ? categoryIdToLabel[item.value]
                  : item.value,
              status: '',
              value: item.value,
            })),
        }))
        ?.filter(({ options }) => options?.length) || [],
    products: response?.pages?.flatMap((page: any) => {
      if (!page?.initialProducts?.products) {
        return [];
      }

      return page?.initialProducts?.products.map((product: any) => ({
        ...product,
        id: Number(product.masterProductId),
        masterProductId: Number(product.masterProductId),
        imageServiceUrl: buildImageServiceUrlFromSku(product.sku),
        // TODO: not present in productsV2 response even if passed into the payload in DEFAULT_GRID_PRODUCT_FIELDS, check BE
        // activeFeaturedProductLocationCodeList
        tagIdList: product?.tagIdList?.map((tag: any) => Number(tag)) || [],
      }));
    }),
  };
};
